import React from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { HelpCircle, MessageSquare, ArrowRight, Sparkles } from 'lucide-react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import CustomPagination from '../../../components/CustomPagination';
import SignInProgressCta from '../../../components/SignInProgressCta';
import { FeedbackModal } from '../../../components/FeedbackModal';
import type { ViewMode } from '../hooks/useViewMode';
import type { SrpPaginationViewModel, SrpStaticStrings } from '../../../features/srp/SrpViewModel';

export interface SearchPaginationProps {
  viewMode: ViewMode;
  helpTrayOpen: boolean;
  setHelpTrayOpen: (open: boolean) => void;
  feedbackModalOpen: boolean;
  setFeedbackModalOpen: (open: boolean) => void;
  pageContextPathname?: string;
  className?: string;
  /** When provided with getPageUrl, render static pagination (same DOM as CustomPagination). */
  staticPagination?: SrpPaginationViewModel;
  getPageUrl?: (page: number) => string;
  /** Pre-generated strings for static SRP (no IntlProvider). When provided with static branch, use instead of useIntl. */
  staticStrings?: SrpStaticStrings;
  /** Current page URL path (pathname + search) for static sign-in CTA returnTo. Pass when static so CTA link matches layout. */
  loginReturnToPath?: string;
}

const baseItemClasses =
  'min-w-[44px] min-h-[44px] md:min-w-[48px] md:min-h-[48px] px-4 md:px-5 py-2.5 md:py-3 rounded-xl md:rounded-2xl text-sm md:text-base font-medium transition-all duration-200 ease-out active:scale-95 flex items-center justify-center touch-manipulation select-none';

/**
 * Search pagination component - contains pagination controls, keyboard shortcuts button, and feedback button.
 * In static mode (staticPagination + getPageUrl) renders same nav structure with anchor links only.
 */
export const SearchPagination: React.FC<SearchPaginationProps> = ({
  viewMode,
  helpTrayOpen,
  setHelpTrayOpen,
  feedbackModalOpen,
  setFeedbackModalOpen,
  pageContextPathname,
  className = '',
  staticPagination,
  getPageUrl,
  staticStrings,
  loginReturnToPath,
}) => {
  if (viewMode === 'map') {
    return null;
  }

  const useStatic = staticPagination != null && getPageUrl != null && staticStrings != null;

  if (useStatic && staticPagination && getPageUrl && staticStrings) {
    const { currentPage, nbPages, prevUrl, nextUrl } = staticPagination;
    const windowSize = 5;
    const half = Math.floor(windowSize / 2);
    let start = Math.max(0, currentPage - half);
    let end = start + (windowSize - 1);
    if (end > nbPages - 1) {
      end = nbPages - 1;
      start = Math.max(0, end - (windowSize - 1));
    }
    const pages = Array.from({ length: end - start + 1 }, (_, i) => start + i);
    const isFirstPage = currentPage <= 0;
    const isLastPage = currentPage >= nbPages - 1;
    const ariaLabel = staticStrings?.paginationAriaLabel ?? 'Job search results pagination';
    const firstLabel = staticStrings?.paginationFirst ?? 'First';
    const prevLabel = staticStrings?.paginationPrevious ?? 'Previous';
    const nextLabel = staticStrings?.paginationNext ?? 'Next';
    const lastLabel = staticStrings?.paginationLast ?? 'Last';

    return (
      <nav
        id="pagination"
        className={`pagination w-full pt-0 lg:pt-8 pb-8 items-center justify-center ${className}`}
        aria-label={ariaLabel}
      >
        <div className="flex justify-center py-4 md:py-6">
          <div className="flex flex-col items-center gap-3 w-full max-w-4xl px-4">
            <div className="flex justify-center items-center gap-2 flex-wrap" role="list">
              {!isFirstPage && (
                <>
                  <a
                    href={getPageUrl(0)}
                    className={`${baseItemClasses} bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 shadow-sm`}
                  >
                    {firstLabel}
                  </a>
                  <a
                    href={prevUrl ?? getPageUrl(currentPage - 1)}
                    className={`${baseItemClasses} bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 shadow-sm`}
                  >
                    <div className="flex items-center gap-1.5">
                      <ChevronLeftIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                      {prevLabel}
                    </div>
                  </a>
                </>
              )}
              {pages.map((page) => {
                const label = page + 1;
                const isActive = currentPage === page;
                return (
                  <a
                    key={page}
                    href={getPageUrl(page)}
                    aria-label={`Page ${label}`}
                    className={`${baseItemClasses} ${isActive
                      ? 'bg-green-500 dark:bg-green-600 text-white font-semibold shadow-md border border-green-600 dark:border-green-500'
                      : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 shadow-sm'
                    }`}
                  >
                    {label}
                  </a>
                );
              })}
              {!isLastPage && (
                <>
                  <a
                    href={nextUrl ?? getPageUrl(currentPage + 1)}
                    className={`${baseItemClasses} bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 shadow-sm`}
                  >
                    <div className="flex items-center gap-1.5">
                      {nextLabel}
                      <ChevronRightIcon className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                    </div>
                  </a>
                  <a
                    href={getPageUrl(nbPages - 1)}
                    className={`${baseItemClasses} bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600 shadow-sm`}
                  >
                    {lastLabel}
                  </a>
                </>
              )}
            </div>
            {/* Match interactive layout: keyboard hint placeholder + feedback + sign-in CTA so page height doesn't jump on swap */}
            <div className="flex justify-center mt-4 gap-2">
              <span
                className="hidden md:flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500"
                aria-hidden
              >
                <HelpCircle className="w-6 h-6" />
              </span>
            </div>
            <div className="flex justify-center mt-4">
              <span
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm"
                aria-hidden
              >
                <MessageSquare className="w-4 h-4" />
                {staticStrings.feedbackButtonLabel}
              </span>
            </div>
            <div className="mt-8 px-4">
              <div className="relative overflow-hidden rounded-[28px] border border-white/40 bg-gradient-to-br from-emerald-400/90 via-green-500/80 to-emerald-600/90 text-white shadow-[0_24px_48px_-20px_rgba(16,185,129,0.6)] dark:border-emerald-300/20 dark:from-emerald-500/70 dark:via-emerald-500/60 dark:to-emerald-600/80">
                <div aria-hidden className="pointer-events-none absolute inset-0">
                  <div className="absolute -left-10 top-6 h-40 w-40 rounded-full bg-white/30 blur-3xl dark:bg-emerald-200/25" />
                  <div className="absolute bottom-[-60px] right-[-20px] h-48 w-48 rounded-full bg-teal-200/35 blur-3xl dark:bg-emerald-300/20" />
                </div>
                <div className="relative flex flex-col gap-4 px-4 py-5 sm:gap-6 sm:px-6 sm:py-8 lg:px-10 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex items-center justify-center lg:justify-start">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-xl shadow-inner shadow-white/10 sm:h-16 sm:w-16">
                      <Sparkles className="h-6 w-6 text-white sm:h-8 sm:w-8" strokeWidth={1.6} />
                    </div>
                  </div>
                  <div className="flex-1 text-center lg:text-left">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-white/80 sm:text-xs md:text-sm">
                      {staticStrings.signInCta.badge}
                    </p>
                    <h2 className="mt-2 text-xl font-semibold sm:mt-3 sm:text-2xl md:text-3xl lg:text-[32px]">
                      {staticStrings.signInCta.title}
                    </h2>
                    <p className="mt-2 text-sm text-white/90 sm:mt-3 sm:text-base md:text-lg">
                      {staticStrings.signInCta.description}
                    </p>
                  </div>
                  <div className="flex w-full justify-center lg:w-auto lg:justify-end">
                    <a
                      href={
                        loginReturnToPath
                          ? `/auth/login?returnTo=${encodeURIComponent(loginReturnToPath)}`
                          : '/auth/login'
                      }
                      className="group relative inline-flex w-full items-center justify-center rounded-2xl bg-white px-4 py-2.5 text-sm font-semibold text-emerald-600 shadow-lg shadow-emerald-900/15 transition-all duration-200 hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-emerald-500 sm:px-5 sm:py-3 sm:text-base md:w-auto"
                    >
                      <span className="flex items-center gap-2">
                        {staticStrings.signInCta.button}
                        <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  const intl = useIntl();
  return (
    <>
      <nav
        id="pagination"
        className={`pagination w-full pt-0 lg:pt-8 pb-8 items-center justify-center ${className}`}
        aria-label={intl.formatMessage({
          id: 'pagination.ariaLabel',
          defaultMessage: 'Job search results pagination',
          description: 'Accessibility label for pagination navigation controls',
        })}
      >
        <CustomPagination />
        <div className="flex justify-center mt-4 gap-2">
          <button
            type="button"
            className="hidden md:flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 hover:bg-green-200 dark:hover:bg-green-400 text-gray-700 dark:text-gray-200 transition-all duration-200 shadow"
            aria-label="Show keyboard shortcuts help"
            onClick={() => setHelpTrayOpen(true)}
            tabIndex={0}
          >
            <HelpCircle className="w-6 h-6" />
            <span className="sr-only">Keyboard Shortcuts</span>
          </button>
        </div>
        <div className="flex justify-center mt-4">
          <button
            type="button"
            onClick={() => setFeedbackModalOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm"
          >
            <MessageSquare className="w-4 h-4" />
            <FormattedMessage
              id="feedback.button.label"
              defaultMessage="Give us feedback"
              description="Button label to open feedback modal"
            />
          </button>
        </div>
        <SignInProgressCta className="mt-8 px-4" />
      </nav>
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onClose={() => setFeedbackModalOpen(false)}
        source="search_results"
        metadata={{
          page: pageContextPathname,
        }}
      />
    </>
  );
};
